/**
 * File: UserProfilePage.tsx
 * Description: Page for viewing and updating the current user's profile
 * Author: Archie Team
 * Created: 2026-05-11
 * Last Modified: 2026-05-11
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  ADMIN_PERSONA,
  LINE_OF_BUSINESS_LABEL,
  LINE_OF_BUSINESS_SECTION_ID,
  PERSONA_LABEL,
  PERSONA_OPTION_ITEMS,
  PERSONA_SECTION_ID,
  PROFILE_SELECTION_HINT,
  isLineOfBusinessRequired,
} from '../../constants/persona';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  UserProfileResponse,
  UserProfileService,
} from '../../services/userProfileService';

import './UserProfilePage.css';
const NOT_SELECTED_PLACEHOLDER = 'Not Selected';
const PROFILE_UPDATED_SUCCESS_MESSAGE = 'Profile updated successfully.';
const PROFILE_UPDATE_ERROR_MESSAGE =
  'Failed to update your profile. Please try again.';
const PROFILE_LOAD_ERROR_MESSAGE = 'Failed to load your profile. Please try again.';
const EMPTY_VALUE = '';

// Shell brand colours used by the integration toggles on this page.
const COLOR_ACCENT_YELLOW = '#ffc600';
const COLOR_BORDER_MEDIUM = 'rgba(28, 28, 28, 0.14)';
const COLOR_TEXT_PRIMARY = '#343434';
const COLOR_TEXT_MUTED = '#616161';

/**
 * Format an ISO date string to a human-readable local date.
 * @param iso - ISO 8601 date string
 * @returns Formatted date string
 */
const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/**
 * UserProfilePage component – displays current user profile info
 * and allows updating the line_of_business field.
 */
export const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { setProfile: setSharedProfile } = useUserProfile();

  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string>(EMPTY_VALUE);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<string>(
    EMPTY_VALUE,
  );
  const [adoEnabled, setAdoEnabled] = useState<boolean>(false);
  const [billyEnabled, setBillyEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAdo, setSavingAdo] = useState(false);
  const [savingBilly, setSavingBilly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const requiresLineOfBusiness = isLineOfBusinessRequired(selectedPersona);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [profileData, optionsData] = await Promise.all([
          UserProfileService.getMyProfile(),
          UserProfileService.getLineOfBusinessOptions(),
        ]);
        setProfile(profileData);
        setLineOfBusinessOptions(optionsData);
        setSelectedPersona(profileData.persona ?? EMPTY_VALUE);
        setSelectedLineOfBusiness(
          profileData.line_of_business ?? EMPTY_VALUE,
        );
        setAdoEnabled(profileData.ado_enabled);
        setBillyEnabled(profileData.billy_enabled);
      } catch {
        setError(PROFILE_LOAD_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!profile) {return;}

    const lineOfBusinessToSave = requiresLineOfBusiness
      ? selectedLineOfBusiness
      : null;

    if (!selectedPersona || (requiresLineOfBusiness && !lineOfBusinessToSave)) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const updated = await UserProfileService.updateProfileSelection({
        persona: selectedPersona,
        line_of_business: lineOfBusinessToSave,
      });
      setProfile(updated);
      setSelectedPersona(updated.persona ?? EMPTY_VALUE);
      setSelectedLineOfBusiness(updated.line_of_business ?? EMPTY_VALUE);
      setSharedProfile(updated);
      setSuccessMessage(PROFILE_UPDATED_SUCCESS_MESSAGE);
    } catch {
      setError(PROFILE_UPDATE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAdo = async (next: boolean): Promise<void> => {
    if (!profile) {return;}
    setSavingAdo(true);
    setError(null);
    setSuccessMessage(null);
    const previous = adoEnabled;
    setAdoEnabled(next);
    try {
      const updated = await UserProfileService.updateAdoEnabled(next);
      setProfile(updated);
      setAdoEnabled(updated.ado_enabled);
      setSharedProfile(updated);
      setSuccessMessage(
        next
          ? 'Azure DevOps integration enabled.'
          : 'Azure DevOps integration disabled.',
      );
    } catch {
      setAdoEnabled(previous);
      setError('Failed to update Azure DevOps preference. Please try again.');
    } finally {
      setSavingAdo(false);
    }
  };

  const handleToggleBilly = async (next: boolean): Promise<void> => {
    if (!profile) {return;}
    setSavingBilly(true);
    setError(null);
    setSuccessMessage(null);
    const previous = billyEnabled;
    setBillyEnabled(next);
    try {
      const updated = await UserProfileService.updateBillyEnabled(next);
      setProfile(updated);
      setBillyEnabled(updated.billy_enabled);
      setSharedProfile(updated);
      setSuccessMessage(
        next ? 'Billy shortcut enabled.' : 'Billy shortcut disabled.',
      );
    } catch {
      setBillyEnabled(previous);
      setError('Failed to update Billy preference. Please try again.');
    } finally {
      setSavingBilly(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-ghost btn-sm"
          aria-label="Go back"
        >
          ← Back
        </button>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        </div>
      )}

      {successMessage && (
        <div role="alert" className="alert alert-success mb-4">
          <span>{successMessage}</span>
        </div>
      )}

      {profile && (
        <div className="card bg-base-100 shadow border border-gray-200">
          <div className="card-body gap-4">
            {/* Avatar */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  {profile.email.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  {profile.email}
                </div>
                <div className="text-sm text-gray-500">
                  Authenticated via EntraID
                </div>
              </div>
            </div>

            {/* Read-only fields */}
            <div className="grid grid-cols-1 gap-3">
              <ProfileField label="Email" value={profile.email} />
              <ProfileField
                label="Member Since"
                value={formatDate(profile.created_at)}
              />
              <ProfileField
                label="Last Updated"
                value={formatDate(profile.updated_at)}
              />
            </div>

            {/* Editable: persona and line_of_business */}
            <div className="pt-4 border-t border-gray-100">
              <label
                htmlFor={PERSONA_SECTION_ID}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {PERSONA_LABEL}
                <span className="ml-1 text-xs text-gray-400 font-normal">
                  {PROFILE_SELECTION_HINT}
                </span>
              </label>
              <div className="relative">
                <select
                  id={PERSONA_SECTION_ID}
                  className="select select-bordered w-full"
                  value={selectedPersona}
                  onChange={(e) => setSelectedPersona(e.target.value)}
                  disabled={saving}
                >
                  <option value="">{NOT_SELECTED_PLACEHOLDER}</option>
                  {PERSONA_OPTION_ITEMS.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.disabled}
                      className={opt.disabled ? 'text-gray-400' : undefined}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {requiresLineOfBusiness && (
              <div className="pt-4 border-t border-gray-100">
                <label
                  htmlFor={LINE_OF_BUSINESS_SECTION_ID}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {LINE_OF_BUSINESS_LABEL}
                  <span className="ml-1 text-xs text-gray-400 font-normal">
                    {PROFILE_SELECTION_HINT}
                  </span>
                </label>
                <div className="relative">
                  <select
                    id={LINE_OF_BUSINESS_SECTION_ID}
                    className="select select-bordered w-full"
                    value={selectedLineOfBusiness}
                    onChange={(e) => setSelectedLineOfBusiness(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">{NOT_SELECTED_PLACEHOLDER}</option>
                    {lineOfBusinessOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="card-actions justify-end pt-2">
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={
                  saving ||
                  !selectedPersona ||
                  (requiresLineOfBusiness && !selectedLineOfBusiness)
                }
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>

            {/* Integrations: Azure DevOps. Only Admins can manage the Codie
                and Billy shortcuts. */}
            {profile.persona === ADMIN_PERSONA && (
              <div className="pt-4 border-t border-gray-100">
                <h2 className="text-base font-semibold text-gray-900 mb-3">
                Integrations
                </h2>
                <div
                  className="flex items-start justify-between gap-4 p-4 rounded-md border"
                  style={{
                    backgroundColor: adoEnabled ? '#fff7b4' : '#ffffff',
                    borderColor: adoEnabled
                      ? COLOR_ACCENT_YELLOW
                      : COLOR_BORDER_MEDIUM,
                    borderLeftWidth: '4px',
                    borderLeftColor: adoEnabled
                      ? COLOR_ACCENT_YELLOW
                      : COLOR_BORDER_MEDIUM,
                    transition:
                    'background-color 160ms ease, border-color 160ms ease',
                  }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="ado-enabled-toggle"
                        className="text-sm font-semibold"
                        style={{ color: COLOR_TEXT_PRIMARY }}
                      >
                      Codie
                      </label>
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: adoEnabled ? COLOR_ACCENT_YELLOW : '#f5f5f5',
                          color: adoEnabled ? COLOR_TEXT_PRIMARY : COLOR_TEXT_MUTED,
                          border: adoEnabled
                            ? `1px solid ${COLOR_ACCENT_YELLOW}`
                            : `1px solid ${COLOR_BORDER_MEDIUM}`,
                        }}
                      >
                        {adoEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p
                      className="text-xs mt-1"
                      style={{ color: COLOR_TEXT_MUTED }}
                    >
                    Push generated architectures to your Azure DevOps backlog.
                    When enabled, a shortcut appears in the top bar.
                    </p>
                  </div>
                  <input
                    id="ado-enabled-toggle"
                    type="checkbox"
                    className="shell-toggle mt-1"
                    checked={adoEnabled}
                    disabled={savingAdo}
                    onChange={(e) => void handleToggleAdo(e.target.checked)}
                    aria-label="Toggle Azure DevOps integration"
                  />
                </div>

                <div
                  className="flex items-start justify-between gap-4 p-4 mt-3 rounded-md border"
                  style={{
                    backgroundColor: billyEnabled ? '#fff7b4' : '#ffffff',
                    borderColor: billyEnabled
                      ? COLOR_ACCENT_YELLOW
                      : COLOR_BORDER_MEDIUM,
                    borderLeftWidth: '4px',
                    borderLeftColor: billyEnabled
                      ? COLOR_ACCENT_YELLOW
                      : COLOR_BORDER_MEDIUM,
                    transition:
                    'background-color 160ms ease, border-color 160ms ease',
                  }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="billy-enabled-toggle"
                        className="text-sm font-semibold"
                        style={{ color: COLOR_TEXT_PRIMARY }}
                      >
                      Billy
                      </label>
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: billyEnabled ? COLOR_ACCENT_YELLOW : '#f5f5f5',
                          color: billyEnabled ? COLOR_TEXT_PRIMARY : COLOR_TEXT_MUTED,
                          border: billyEnabled
                            ? `1px solid ${COLOR_ACCENT_YELLOW}`
                            : `1px solid ${COLOR_BORDER_MEDIUM}`,
                        }}
                      >
                        {billyEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <p
                      className="text-xs mt-1"
                      style={{ color: COLOR_TEXT_MUTED }}
                    >
                    Open Billy, the business UI to chat the feasibility and
                    costs of a project. When enabled, a shortcut appears in the
                    top bar.
                    </p>
                  </div>
                  <input
                    id="billy-enabled-toggle"
                    type="checkbox"
                    className="shell-toggle mt-1"
                    checked={billyEnabled}
                    disabled={savingBilly}
                    onChange={(e) => void handleToggleBilly(e.target.checked)}
                    aria-label="Toggle Billy shortcut"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Small read-only field row. */
const ProfileField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1">
    <span className="text-sm font-medium text-gray-500 sm:w-36 shrink-0">
      {label}
    </span>
    <span className="text-sm text-gray-900 break-all">{value}</span>
  </div>
);

export default UserProfilePage;
